import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { BetterAuthError } from "../utils";
import { mcpFetch } from "./mcpFetch";
import { type McpClientWithSecret, mcpClientWithSecretSchema } from "./schemas";
import { MCP_CLIENTS_QUERY_KEY } from "./useMcpClients";

export type RotateMcpClientSecretInput = {
  clientId: string;
};

/**
 * Issues a new secret for an existing credential, keeping its client ID. Secrets are stored hashed,
 * so this is also the only recovery from a lost one — the old value can't be read back.
 */
export function useRotateMcpClientSecret(
  options?: UseMutationOptions<
    McpClientWithSecret,
    BetterAuthError,
    RotateMcpClientSecretInput,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ clientId }: RotateMcpClientSecretInput): Promise<McpClientWithSecret> =>
      mcpFetch(
        "/oauth2/client/rotate-secret",
        mcpClientWithSecretSchema,
        { method: "POST", body: { client_id: clientId } },
        "Could not rotate the secret",
      ),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: MCP_CLIENTS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}
